import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Inquiry, isBookingFromAirbnb, isBookingFromExpediaAPI } from '@tv3/store/inquiry/inquiry.model'
import { Message } from '@tv3/store/message/message.model'
import * as R from 'ramda'
import * as lodash from 'lodash'
import { AddMessageComplete, GetBookingMessages, AddMessage } from '@tv3/store/message/message.actions'
import {
  Account,
  AccountGuard,
  AlertDialogService,
  Attachment,
  ChannelService,
  Destroyable,
  epochToView,
  getAttachmentLinks,
  isSomething,
  selectCurrentAccount,
  selectUsers,
  SessionStorage,
  Toaster,
  UploadBoxOptions,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import * as fromRoot from '@tv3/store/state'
import { Branding } from '@tv3/models/account/branding'
import { AttachmentDialogService } from '@tv3/shared/attachment/attachment-dialog.service'
import { selectTemplatesByTypes, Template, TemplateType } from '@tokeet-frontend/templates'
import { selectInquiries, selectInquiry } from '@tv3/store/inquiry/inquiry.selectors'
import { DataDictService } from '@tv3/services/data-dict.service'
import { Website } from '@tv3/store/website/website.model'
import { selectWebsitesForRental } from '@tv3/store/website/website.selectors'
import { Actions, ofType } from '@ngrx/effects'
import { debounceTime, filter, finalize, map, switchMap, tap } from 'rxjs/operators'
import { DataCheckerService } from '@tokeet-frontend/tv3-platform'
import { TemplateGuard } from '@tv3/guards/template.guard'
import { UserGuard } from '@tv3/guards/user.guard'
import { PreferencesGuard } from '@tv3/guards/preferences.guard'
import { Guest } from '@tv3/store/guest/guest.model'
import { SearchInquiries } from '@tv3/store/inquiry/inquiry.actions'
import { InquiryGuard } from '@tv3/guards/inquiry.guard'
import { AuthService } from '@tv3/services/auth.service'
import { DataDictionaryDialogService } from '@tv3/shared/data-dictionary-dialog/data-dictionary-dialog.service'
import { WebsiteGuard } from '@tv3/guards/website.guard'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import { AiResponderService } from '@tv3/containers/ai-responder/store/ai-responder.service'
import { environment } from '@tv3/environments/environment'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import { TinyMceEditor, TinyMceEditorComponent, TinyMceEditorOptions } from '@tokeet-frontend/tv3-platform'
import { MessageEditorFeatures, MessagingChannelGuard, MessagingChannelType } from '@tokeet-frontend/message'
import { SmartDeviceAccess, SmartDeviceAccessService } from '@tokeet-frontend/smart-devices'
import { EntityAttributeGuard } from '@tokeet-frontend/custom-attrs'
import { MessagingChannelSwitcherComponent } from './components/messaging-channel-switcher/messaging-channel-switcher.component'
import { isAddingMessage } from '@tv3/store/message/message.selectors'

export function getLocalBookingMessageDraftKey(inquiryId: string) {
  return `${inquiryId}-message-draft`
}

export function getLocalBookingMessageDraftSummaryKey(inquiryId: string) {
  return `${inquiryId}-message-draft-summary`
}

@Component({
  selector: 'app-inquiry-message-form',
  templateUrl: './inquiry-message-form.component.html',
  host: { class: 'd-flex flex-column message-editor comment-composer-bottom-fixed bg-default2' },
  styleUrls: ['./inquiry-message-form.component.scss'],
})
export class InquiryMessageFormComponent extends Destroyable implements OnInit, AfterViewInit {
  @ViewChild('tinyMceEditor') tinyMceEditor: TinyMceEditorComponent
  @ViewChild(MessagingChannelSwitcherComponent) messagingChannel: MessagingChannelSwitcherComponent

  @Input() inquiry: Inquiry
  @Input() guest?: Guest
  @Input() minEditorHeight = 250
  @Input() maxEditorHeight = 300
  @Input() draftMessage?: Message

  @Output() close = new EventEmitter()
  @Output() popup = new EventEmitter()

  @Input()
  @HostBinding('class.popup')
  isPopup = false

  get localBookingMessageDraftKey() {
    return getLocalBookingMessageDraftKey(this.inquiry.id)
  }

  get localAiMessageSummarKey() {
    return getLocalBookingMessageDraftSummaryKey(this.inquiry.id)
  }

  get guestEmail() {
    return this.inquiry?.guest?.primaryEmail || this.inquiry?.guestDetails?.email
  }

  get guestId() {
    return this.inquiry?.guestId || this.guest?.id
  }

  get isSenable() {
    return this.messagingChannel?.selectedChannel !== MessagingChannelType.Email || !!this.inquiry?.id
  }

  get isPlainTextMode() {
    const messagingChannel = this.messagingChannel?.selectedChannel
    const isRichTextChannel = [MessagingChannelType.Email, MessagingChannelType.Gmail].includes(messagingChannel)
    const isPlainTextCM = [isBookingFromExpediaAPI].some((fn) => fn(this.inquiry))

    return isPlainTextCM || !isRichTextChannel
  }

  form = this.fb.group({
    subject: [''],
    cc: [[]],
    bcc: [[]],
    addCost: [false],
    template: [''],
  })

  attachments: Attachment[] = []
  dragDropUploadOptions: UploadBoxOptions
  isDragDropEnabled = true

  messageEditorCtrl = new FormControl('', [Validators.required])

  availableEditorFeatures: MessageEditorFeatures[] = []

  inquiryCtrl = new FormControl('')

  branding: Branding
  rentalWebsites: Website[] = []
  account: Account
  deviceAccesses: SmartDeviceAccess[] = []

  hasCC = false
  hasBCC = false
  showEditor = false
  showHeader = false

  isAIGenerating = false
  isAISubscriptionValid = false
  aiMessageSummary = ''

  users$ = this.store.pipe(select(selectUsers))
  inquiries$: Observable<{ id: string; name: string }[]>
  private readonly emailTemplateTypes = [TemplateType.Email]
  private readonly smsTemplateTypes = [TemplateType.PushNotification]
  private readonly selectedTemplateTypes$ = new BehaviorSubject<TemplateType[]>(this.emailTemplateTypes)
  templates$ = combineLatest([
    this.store.pipe(select(selectTemplatesByTypes([...this.emailTemplateTypes, ...this.smsTemplateTypes]))),
    this.selectedTemplateTypes$,
  ]).pipe(map(([templates, selectedTypes]) => templates.filter((template) => selectedTypes.includes(template.type))))
  isAddingMessage$ = this.store.pipe(select(isAddingMessage))
  tinyMceOptions: TinyMceEditorOptions | null = null

  inquirySearch = new BehaviorSubject<string>('')

  constructor(
    private fb: FormBuilder,
    private toaster: Toaster,
    private actions$: Actions,
    private storage: SessionStorage,
    private dataChecker: DataCheckerService,
    private attachmentDialog: AttachmentDialogService,
    private channelService: ChannelService,
    private alertDialog: AlertDialogService,
    private dataDictService: DataDictService,
    private deviceAccessService: SmartDeviceAccessService,
    private auth: AuthService,
    private dataDictionaryDialogService: DataDictionaryDialogService,
    private store: Store<fromRoot.State>,
    private aiResponder: AiResponderService,
    private ngZone: NgZone
  ) {
    super()
    this.dataChecker.check([
      TemplateGuard,
      UserGuard,
      PreferencesGuard,
      InquiryGuard,
      AccountGuard,
      WebsiteGuard,
      MessagingChannelGuard,
      EntityAttributeGuard,
    ])
  }

  ngOnInit() {
    this.inquirySearch
      .pipe(
        filter((name) => !R.isEmpty(name)),
        debounceTime(375),
        untilDestroy(this)
      )
      .subscribe((term) => {
        this.store.dispatch(SearchInquiries({ term }))
      })

    this.store
      .pipe(select(selectActiveProductPlanView(ProductsForPlan.TokeetAI)), untilDestroy(this))
      .subscribe((res) => {
        this.isAISubscriptionValid = !!res?.isValid
      })

    this.store
      .pipe(select(selectInquiry, { id: this.inquiry.id }), filter(isSomething), untilDestroy(this))
      .subscribe((inquiry) => {
        this.inquiry = inquiry
      })

    this.deviceAccessService.getBookingDeviceAccesses(this.inquiry.id).subscribe(
      (items) => {
        this.deviceAccesses = items
      },
      () => {}
    )

    this.store
      .pipe(select(selectWebsitesForRental, { id: this.inquiry.rentalId }), untilDestroy(this))
      .subscribe((webs) => {
        this.rentalWebsites = webs
      })

    this.store.pipe(select(selectCurrentAccount), untilDestroy(this)).subscribe((account) => {
      this.account = account
    })

    this.actions$.pipe(ofType(AddMessageComplete), untilDestroy(this)).subscribe(() => {
      this.storage.remove(this.localBookingMessageDraftKey)
      this.storage.remove(this.localAiMessageSummarKey)
      this.close.emit(true)
    })

    this.initForm(this.inquiry)
    this.setDragDropUploadOptions()
    this.tryToAlertRestrictedEmail()
    this.messageEditorCtrl.valueChanges.pipe(debounceTime(500), untilDestroy(this)).subscribe((msg) => {
      this.storage.set(this.localBookingMessageDraftKey, msg)
    })

    if (this.guest) {
      this.inquiryCtrl.patchValue(this.inquiry.id)
      this.inquiries$ = this.store.pipe(
        select(selectInquiries),
        map((inquiries) => R.filter((i: Inquiry) => i.guestId === this.guest.id, inquiries)),
        map((inquiries) =>
          R.map((i: Inquiry) => {
            const createdView = epochToView(i.created)
            return {
              id: i.id,
              name: `${R.pathOr('', ['guestDetails', 'name'], i)} / ${R.pathOr(
                '',
                ['rental', 'name'],
                i
              )} / ${createdView}`,
            }
          }, inquiries)
        )
      )
      this.inquiryCtrl.valueChanges
        .pipe(
          switchMap((id) => this.store.pipe(select(selectInquiry, { id }))),
          untilDestroy(this)
        )
        .subscribe((inquiry) => {
          this.initForm(inquiry)
          this.setDragDropUploadOptions()
        })
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.showEditor = true
      this.updateTinyMceOptions()
    }, 0)
  }

  compileDataTokens(content: string) {
    return this.dataDictService.resolve(
      content,
      {
        inquiry: this.inquiry,
        rental: this.inquiry?.rental,
        guest: this.inquiry?.guest,
        websites: this.rentalWebsites,
        account: this.account,
        accesses: this.deviceAccesses,
      },
      {},
      { plainText: this.isPlainTextMode }
    )
  }

  enableQuote(inquiry: Inquiry) {
    const source = inquiry.inquirySource
    return !/airbnb|booking/gi.test(source)
  }

  initForm(inquiry: Inquiry) {
    const localMsg = this.storage.get(this.localBookingMessageDraftKey, '')
    const draftMessageContent = this.draftMessage?.messageHtml
    const defaultHtml = (this.isPopup ? localMsg : draftMessageContent) || ''

    this.setAiResponderSummary(
      this.isPopup
        ? this.storage.get(this.localAiMessageSummarKey, '')
        : this.draftMessage?.attributes?.aiResponder?.summary || ''
    )
    this.form.patchValue({ subject: this.getSubject(inquiry) })
    this.setEditorValue(defaultHtml)
  }

  openDataDictionary() {
    return this.dataDictionaryDialogService.open().pipe(switchMap((tag) => this.compileDataTokens(tag)))
  }

  onPopup() {
    this.popup.emit(true)
    document.body.classList.add('message-form-popup')
  }

  onClose() {
    this.close.emit(true)
    document.body.classList.remove('message-form-popup')
  }

  onAiResponder() {
    if (!this.isAISubscriptionValid) {
      window.open(`${environment.appStoreUrl}/login?redirect=/details/tokeet_ai&token=${this.auth.token}`, '_blank')
      return
    }

    this.isAIGenerating = true
    this.aiResponder
      .generateReply({
        account: this.inquiry.account,
        inquiryId: this.inquiry?.id,
        convert: this.isPlainTextMode ? undefined : 'html',
        create: 'draft',
      })
      .pipe(finalize(() => (this.isAIGenerating = false)))
      .subscribe(
        ({ message, summary }) => {
          this.setEditorValue(this.isPlainTextMode ? this.convertPlainTextToHtml(message) : message)
          this.store.dispatch(GetBookingMessages({ bookingId: this.inquiry?.id }))
          this.setAiResponderSummary(summary)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  setAiResponderSummary(summary: string) {
    this.aiMessageSummary = summary
    this.storage.set(this.localAiMessageSummarKey, summary)
  }

  onTemplateChange([template]: [Template]) {
    if (template) {
      combineLatest([this.compileDataTokens(template.body), this.compileDataTokens(template.subject)]).subscribe(
        ([body, subject]) => {
          this.form.patchValue({ subject: subject || '' })
          this.setEditorValue(body)
        }
      )
    }
  }

  onSend(form: FormGroup) {
    if (!this.isMessageValid(this.inquiry, this.getEditorText())) {
      return
    }

    if (!this.messagingChannel.validateSelectedChannel(this.messagingChannel.selectedChannel)) {
      return
    }

    const attachments = this.attachments
    const messageHtml = this.getMessageHtml()
    const messageText = this.getMessageText()

    const serializedAttachments = attachments.map((t) => (lodash.isFunction(t.serialize) ? t.serialize() : t))
    this.store.dispatch(
      AddMessage({
        inquiry: this.inquiry,
        form: {
          ...form.value,
          messageText,
          messageHtml,
          attachments: serializedAttachments,
          whatsapp_phone: this.guest?.whatsapp_phone,
          phone: this.guest?.phone,
        },
        channel: this.messagingChannel.selectedChannel,
      })
    )
  }

  private tryToAlertRestrictedEmail() {
    const channel = this.channelService.restrictedChannelFromEmail(this.guestEmail)
    if (!channel) {
      return
    }

    this.alertDialog.open({
      title: $localize`Limited messaging ability`,
      body:
        $localize`Your message will be routed through ` +
        channel +
        +`.<br>` +
        $localize`As a result you are restricted in what you may include` +
        `<br>` +
        $localize`in your message. Links, attachments, and email addresses` +
        `<br>` +
        $localize`are not allowed. ` +
        channel +
        $localize` will block any message containing` +
        `<br>` +
        $localize`restricted content.`,
    })
  }

  private getMessageHtml(): string {
    if (!this.attachments?.length) {
      return this.getEditorHtml()
    }
    return `${this.getEditorHtml()}<br><br>${this.getAttachmentsHtml(this.attachments)}`
  }

  private getMessageText(): string {
    if (!this.attachments?.length) {
      return this.getEditorText()
    }
    return `${this.getEditorText()}\n\n${this.getAttachmentsPlainText(this.attachments)}`
  }

  private isMessageValid(inquiry: Inquiry, messageText: string) {
    if (!inquiry.convoId || !inquiry.guestId) {
      this.alertDialog.open({
        title: $localize`Error`,
        body: $localize`There is an error in your inquiry.`,
      })
      return false
    }

    // TV4-5971: inline editor images (e.g. template-embedded photos) leave only
    // whitespace in the plain text, which used to slip past the raw length
    // check and reach Airbnb as an empty message. A message with images or
    // attachments but no text is valid only where they actually deliver:
    // Airbnb API threads send Cloudinary-hosted images as media messages
    // (matching the backend allowlist), plain email carries any inline image
    // in its HTML body, and restricted relays (Booking.com, Expedia, ...) plus
    // the plain-text channel transports (SMS, WhatsApp, Gmail, ...) carry
    // text only.
    const isClassicChannel = this.messagingChannel?.selectedChannel === MessagingChannelType.Email
    const isAirbnbApi = this.inquiry?.attributes?.api === 'airbnbapiv2'
    const isRestrictedRelay = !!this.channelService.restrictedChannelFromEmail(this.guestEmail)
    const allowsImages = isClassicChannel && (isAirbnbApi || !isRestrictedRelay)
    const imageRegex = isAirbnbApi ? /<img[^>]*\bsrc\s*=\s*["']https:\/\/res\.cloudinary\.com\//i : /<img[\s>]/i
    const trimmedText = (messageText || '').trim()
    const hasAttachments = allowsImages && !!this.attachments?.length
    const hasInlineImages = allowsImages && imageRegex.test(this.getEditorHtml() || '')
    if (trimmedText.length < 3 && !hasAttachments && !hasInlineImages) {
      this.alertDialog.open({
        title: $localize`Error`,
        body: $localize`Your message must be over 2 characters or include an attachment.`,
      })
      return false
    }

    return true
  }

  onEditorSetup(editor: TinyMceEditor) {
    editor.ui.registry.addButton('datadic', {
      icon: 'book',
      tooltip: $localize`You may insert data tokens using data dictionary.`,
      onAction: () =>
        this.ngZone.run(() => {
          this.openDataDictionary().subscribe((tag) => editor.insertContent(tag))
        }),
    })

    editor.ui.registry.addButton('subject', {
      icon: 'settings',
      tooltip: $localize`Toggle headers`,
      onAction: () =>
        this.ngZone.run(() => {
          this.showHeader = !this.showHeader
        }),
    })

    editor.on('change', () => {
      this.setAiResponderSummary('')
    })
  }

  onAttachment() {
    this.isDragDropEnabled = false
    this.attachmentDialog
      .open({ allowedFileExtensions: this.getAllowedFileExtensions() })
      .pipe(
        filter(isSomething),
        tap((attachments: Attachment[]) => {
          this.attachments = [...this.attachments, ...attachments]
        })
      )
      .subscribe(() => {
        this.isDragDropEnabled = true
      })
  }

  onDragDropUploadComplete(uploadedAttachments: Attachment[]) {
    if (!uploadedAttachments?.length) {
      return
    }
    this.attachments = [...this.attachments, ...uploadedAttachments]
  }

  removeAttachment(attachment: Attachment) {
    this.attachments = this.attachments.filter((item) => item.id !== attachment.id)
  }

  onEditorFeaturesChange(features: MessageEditorFeatures[]) {
    this.availableEditorFeatures = features
    this.isDragDropEnabled = features.includes('attachment')
    this.selectedTemplateTypes$.next(this.getTemplateTypesForChannel(this.form.get('channel')?.value))
    this.updateTinyMceOptions()
  }

  private getTemplateTypesForChannel(channel: MessagingChannelType): TemplateType[] {
    return [MessagingChannelType.SMS, MessagingChannelType.Whatsapp].includes(channel)
      ? this.smsTemplateTypes
      : this.emailTemplateTypes
  }

  private convertPlainTextToHtml(text: string) {
    if (!text) {
      return ''
    }
    return text.replace(/\r\n/g, '\n').split('\n').join('<br />')
  }

  private setEditorValue(value: string) {
    this.messageEditorCtrl?.setValue(value)
  }

  private getEditorHtml() {
    return this.tinyMceEditor?.editor?.getContent({ format: 'html' })
  }

  private getEditorText() {
    return this.tinyMceEditor?.editor?.getContent({ format: 'text' })
  }

  private getAttachmentsHtml(attachments: Attachment[]) {
    return getAttachmentLinks(attachments, this.guestId)
  }

  private getAttachmentsPlainText(attachments: Attachment[]) {
    return getAttachmentLinks(attachments, this.guestId, true)
  }

  private updateTinyMceOptions() {
    const options: TinyMceEditorOptions = {}
    if (this.isPlainTextMode) {
      options.toolbar = 'undo redo | datadic | subject'
      options.paste_as_text = true
    }
    this.tinyMceOptions = Object.keys(options).length ? options : null
  }

  private setDragDropUploadOptions() {
    this.dragDropUploadOptions = {
      maxSize: 10 * 1024 * 1024,
      maxUploads: 10,
      autoUpload: true,
      token: this.auth.token,
      allowedFileExtensions: this.getAllowedFileExtensions() || undefined,
    }
  }

  getAllowedFileExtensions() {
    if (isBookingFromExpediaAPI(this.inquiry)) {
      return ['.pdf', '.png', '.jpeg']
    }
    return null
  }

  getAttachmentTooltip() {
    const exts = this.getAllowedFileExtensions()
    if (exts) {
      return $localize`Only ${exts.join(', ')} files are allowed.`
    }
    return $localize`Attachments`
  }

  addNewCC = (email: string) => {
    email = email.toLowerCase()
    const EMAIL_REGEXP =
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/
    if (EMAIL_REGEXP.test(email)) {
      return email
    } else {
      this.alertDialog.open({
        title: $localize`Cannot send to that email.`,
        body: $localize`The email you entered is invalid. AdvanceCM cannot send messsages to invalid email addresses. Please enter a valid email address to add to the CC of this message.`,
      })
    }
  }

  private getSubject(inquiry: Inquiry): string {
    const rentalView = R.pathOr('', ['rentalView'], inquiry)
    const rentalName = R.pathOr(rentalView, ['rental', 'name'], inquiry)
    if (inquiry?.id) {
      return `Re: Your inquiry about ${rentalName} (${inquiry.guestNameView}) - ${inquiry?.id?.substr(
        inquiry?.id?.length - 5
      )}`
    } else {
      return 'Re: Your inquiry'
    }
  }
}
